import React, { useCallback, useEffect, useState } from "react";
import useContract from "../../hooks/useContract";
import { Page, Panel, Stat, Progress, formatRaw, formatInteger, formatDate, tierStatus, getError, USD_DECIMALS } from "./ktlShared";

const League = () => {
  const { getCurrentLeague, getAllTiers, getExtendedGlobalStats, getRewardTokenData } = useContract();
  const [data, setData] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [extended, setExtended] = useState(null);
  const [token, setToken] = useState({ symbol: "KIND", decimals: 18 });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const [current, allTiers, stats, reward] = await Promise.all([
        getCurrentLeague(), getAllTiers(), getExtendedGlobalStats(), getRewardTokenData()
      ]);
      setData(current);
      setTiers([{ id: 1, data: allTiers.tier1 }, { id: 2, data: allTiers.tier2 }, { id: 3, data: allTiers.tier3 }]);
      setExtended(stats);
      setToken(reward);
    } catch (e) { setError(getError(e)); }
  }, [getAllTiers, getCurrentLeague, getExtendedGlobalStats, getRewardTokenData]);

  useEffect(() => { load(); }, [load]);

  const league = data?.league;

  return (
    <Page eyebrow="League status" title={`League #${data?.leagueId?.toString() || "0"}`} description="League period, global activity, tier activation and carried rewards.">
      {error && <div className="mb-5 rounded-xl bg-red-400/10 p-4 text-red-300">{error}</div>}
      <Panel className="mb-6 bg-gradient-to-br from-[#111314] to-[#29240d]">
        <div className="flex flex-col justify-between gap-5 md:flex-row">
          <div><p className="text-xs text-[#ffcc66]">{league?.active ? "ACTIVE" : league?.finalized ? "FINALIZED" : "INACTIVE"}</p><h2 className="mt-2 text-2xl font-black">Monthly KIND Trading Competition</h2><p className="mt-2 text-sm text-[#777]">{formatDate(league?.startAt)} — {formatDate(league?.endAt)}</p></div>
          <div><p className="text-xs text-[#777]">Qualified wallets</p><p className="mt-2 text-3xl font-black text-[#ffcc66]">{formatInteger(league?.totalQualifiedWallets)}</p></div>
        </div>
      </Panel>

      <div className="mb-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Trading Volume" value={`$${formatRaw(league?.totalVolumeUsd, USD_DECIMALS)}`} accent />
        <Stat label="Total KP" value={`${formatInteger(league?.totalKp)} KP`} />
        <Stat label="Reward Pool" value={`${formatRaw(league?.rewardPool, token.decimals)} ${token.symbol}`} accent />
        <Stat label="Pending Pools" value={`${formatRaw(extended?.pendingRewardPools, token.decimals)} ${token.symbol}`} />
      </div>

      <div className="space-y-4">
        {tiers.map(({ id, data: tier }) => {
          const status = tierStatus(tier);
          const remaining = Math.max(Number(tier?.minWallets || 0) - Number(tier?.qualifiedWallets || 0), 0);
          return (
            <Panel key={id}>
              <div className="flex justify-between gap-4">
                <div><p className="text-xs text-[#ffcc66]">TIER {id}</p><h3 className="mt-1 text-xl font-black">{id === 1 ? "Ranks 1–10" : id === 2 ? "Ranks 11–50" : "Ranks 51+"}</h3></div>
                <span className={`h-fit rounded-full border px-3 py-1 text-[9px] ${status.cls}`}>{status.label}</span>
              </div>
              <div className="mt-5"><Progress value={tier?.qualifiedWallets} max={tier?.minWallets} /></div>
              <p className="mt-3 text-xs text-[#ffcc66]">{remaining ? `${remaining} more wallets required.` : "Minimum reached."}</p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Current Pool" value={`${formatRaw(tier?.currentRewardPool, token.decimals)} ${token.symbol}`} />
                <Stat label="Pending Pool" value={`${formatRaw(tier?.pendingRewardPool, token.decimals)} ${token.symbol}`} />
                <Stat label="Current KP" value={`${formatInteger(tier?.currentLeagueKp)} KP`} />
                <Stat label="Pending KP" value={`${formatInteger(tier?.pendingKp)} KP`} />
              </div>
            </Panel>
          );
        })}
      </div>
    </Page>
  );
};

export default League;
